/**
 * The in-memory pool every synced view reads from
 * (back-end-rs/docs/SYNC_ENGINE.md §7).
 *
 * Two layers. **The server's tables** are what the engine has sent, and
 * are what IndexedDB keeps. **The overlay** is this tab's own writes that
 * the engine has not yet echoed back (`writes.ts`): field values laid over
 * the server's row. Views read the two merged, so a change shows the
 * moment it is made rather than on its round trip.
 *
 * Tables are replaced, never mutated: applying a packet builds new Maps
 * for the tables it touches and leaves the others alone. So a table's
 * identity changes exactly when its rows do, which is the contract
 * `useSyncExternalStore` needs — and a view over homes does not re-render
 * because a vendor changed.
 */
import {
  BootstrapPayload,
  MODEL_NAMES,
  ModelArrays,
  ModelName,
  SyncAction,
  SyncRows,
} from './models';

type Tables = { [K in ModelName]: ReadonlyMap<string, SyncRows[K]> };

/** Pending field values for some rows of one model. */
export type Overlay = ReadonlyMap<string, Readonly<Record<string, unknown>>>;

const emptyTables = (): Tables =>
  Object.fromEntries(MODEL_NAMES.map((m) => [m, new Map()])) as unknown as Tables;

const rowId = (row: unknown): string => String((row as { id: unknown }).id);

export class SyncStore {
  /** What the engine has sent. */
  private server: Tables = emptyTables();
  /** `server` with the overlay laid over it: what views read. */
  private view: Tables = emptyTables();
  private overlays = new Map<ModelName, Overlay>();
  private ready = false;
  /** Inside `serverChanged`: an overlay set now is merged by the caller. */
  private settling = false;
  private listeners = new Set<() => void>();
  private serverListeners = new Set<(models: ModelName[]) => void>();
  /** The last sync id this copy reflects; null until there is a copy. */
  lastSyncId: number | null = null;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /**
   * Told which models' server rows just changed — a packet, a bootstrap —
   * before views are. The write queue uses it to retire writes the engine
   * has now echoed back.
   */
  onServerChange = (listener: (models: ModelName[]) => void): (() => void) => {
    this.serverListeners.add(listener);
    return () => {
      this.serverListeners.delete(listener);
    };
  };

  /** True once the pool holds a copy — from IndexedDB or a bootstrap. */
  isReady = (): boolean => this.ready;

  /** A model's rows as views should show them: the overlay included. */
  table<K extends ModelName>(model: K): ReadonlyMap<string, SyncRows[K]> {
    return this.view[model];
  }

  /** A row as the server last sent it, without this tab's pending writes. */
  serverRow<K extends ModelName>(model: K, id: string): SyncRows[K] | undefined {
    return this.server[model].get(id);
  }

  /** Replace everything: a bootstrap, or a copy restored from IndexedDB. */
  replace(payload: Pick<BootstrapPayload, 'lastSyncId' | 'models'>): void {
    const next = emptyTables() as { [K in ModelName]: Map<string, SyncRows[K]> };
    for (const model of MODEL_NAMES) {
      const rows = (payload.models[model] ?? []) as SyncRows[typeof model][];
      next[model] = new Map(rows.map((r) => [rowId(r), r])) as never;
    }
    this.server = next;
    this.lastSyncId = payload.lastSyncId;
    this.ready = true;
    this.serverChanged(MODEL_NAMES);
    this.view = Object.fromEntries(
      MODEL_NAMES.map((m) => [m, this.merge(m)])
    ) as unknown as Tables;
    this.emit();
  }

  /** Apply one packet as one batch, with one notification. */
  apply(actions: SyncAction[], syncId: number): void {
    const touched = new Map<ModelName, Map<string, unknown>>();
    for (const a of actions) {
      if (!MODEL_NAMES.includes(a.model)) continue;
      let t = touched.get(a.model);
      if (!t) {
        t = new Map(this.server[a.model] as ReadonlyMap<string, unknown>);
        touched.set(a.model, t);
      }
      if (a.op === 'upsert' && a.data) t.set(a.rowId, a.data);
      else if (a.op === 'delete') t.delete(a.rowId);
    }
    this.lastSyncId = Math.max(this.lastSyncId ?? 0, syncId);
    if (touched.size === 0) return;
    this.server = { ...this.server, ...Object.fromEntries(touched) } as Tables;
    const models = Array.from(touched.keys());
    this.serverChanged(models);
    this.remerge(models);
    this.emit();
  }

  /**
   * Set one model's overlay — every pending write for it, as the field
   * values each row should show. An empty map clears it.
   */
  setOverlay(model: ModelName, overlay: Overlay): void {
    if (overlay.size === 0 && !this.overlays.has(model)) return;
    if (overlay.size === 0) this.overlays.delete(model);
    else this.overlays.set(model, overlay);
    if (this.settling) return;
    this.remerge([model]);
    this.emit();
  }

  /** Advance the cursor without data (the socket's `live` message). */
  advance(syncId: number): void {
    this.lastSyncId = Math.max(this.lastSyncId ?? 0, syncId);
  }

  /** Sign-out, or a different user: nothing may survive. */
  clear(): void {
    this.server = emptyTables();
    this.view = emptyTables();
    this.overlays.clear();
    this.lastSyncId = null;
    this.ready = false;
    this.emit();
  }

  /** The server's rows, as arrays — what IndexedDB keeps. Pending writes
   *  are kept separately, by the write queue. */
  snapshot(): { lastSyncId: number; models: ModelArrays } | null {
    if (!this.ready || this.lastSyncId === null) return null;
    const models = Object.fromEntries(
      MODEL_NAMES.map((m) => [
        m,
        Array.from((this.server[m] as ReadonlyMap<string, unknown>).values()),
      ])
    ) as unknown as ModelArrays;
    return { lastSyncId: this.lastSyncId, models };
  }

  /** A model's view: its server table, or a copy with the overlay laid
   *  over the rows it names. A pending write to a row the server no
   *  longer has shows nothing — the row is gone. */
  private merge<K extends ModelName>(model: K): ReadonlyMap<string, SyncRows[K]> {
    const base = this.server[model];
    const overlay = this.overlays.get(model);
    if (!overlay || overlay.size === 0) return base;
    const out = new Map(base);
    overlay.forEach((fields, id) => {
      const row = base.get(id);
      if (row) out.set(id, { ...row, ...fields } as SyncRows[K]);
    });
    return out;
  }

  private remerge(models: ModelName[]): void {
    const next = { ...this.view } as { [K in ModelName]: ReadonlyMap<string, SyncRows[K]> };
    models.forEach((m) => {
      next[m] = this.merge(m) as never;
    });
    this.view = next as Tables;
  }

  /** Tell the write queue first, so the one merge and the one
   *  notification that follow already reflect writes it retired. Every
   *  caller re-merges `models` afterwards; an overlay set for another
   *  model is merged here. */
  private serverChanged(models: ModelName[]): void {
    const before = new Map(this.overlays);
    this.settling = true;
    try {
      this.serverListeners.forEach((l) => l(models));
    } finally {
      this.settling = false;
    }
    const others = MODEL_NAMES.filter(
      (m) => !models.includes(m) && before.get(m) !== this.overlays.get(m)
    );
    if (others.length > 0) this.remerge(others);
  }

  private emit(): void {
    this.listeners.forEach((l) => l());
  }
}

/** The one pool the app reads. */
export const syncStore = new SyncStore();
